import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db, messagingPromise } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/** Request browser notification permission and obtain the FCM token. */
export async function requestPermissionAndGetToken() {
  if (!('Notification' in window)) return null;

  const messaging = await messagingPromise;
  if (!messaging) return null;

  if (!VAPID_KEY) {
    console.warn('[FCM] REACT_APP_FIREBASE_VAPID_KEY is not set.');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    return await getToken(messaging, { vapidKey: VAPID_KEY });
  } catch (err) {
    console.warn('[FCM] getToken failed:', err);
    return null;
  }
}

/** Persist the FCM token to Firestore under users/{uid}. */
export async function saveTokenForUser(uid, token) {
  if (!db || !uid || !token) return;
  try {
    await setDoc(doc(db, 'users', uid), { fcmToken: token }, { merge: true });
  } catch (err) {
    console.warn('[FCM] saveToken failed:', err);
  }
}

/** Remove the FCM token from Firestore on sign-out. */
export async function removeTokenForUser(uid) {
  if (!db || !uid) return;
  try {
    await updateDoc(doc(db, 'users', uid), { fcmToken: deleteField() });
  } catch {
    // document may not exist yet — ignore
  }
}

/**
 * Register a handler for foreground messages (app is open).
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  const messaging = await messagingPromise;
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}

/** Convenience: request permission, get token, and save to Firestore. */
export async function initFcmForUser(uid) {
  const token = await requestPermissionAndGetToken();
  if (token) await saveTokenForUser(uid, token);
  return token;
}
